import { cn } from '@discover-lib/utils';
import { type SuburbType } from '@discover-data/gql/fragments/suburb';
import { AccordionRoot } from '@discover-ui/accordion';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import TagsAccordionItem from './tags/accordionItem';
import overloadTags from './tags/overloadTags';
import SuburbAccordionItem from './suburb/accordionItem';

const ignoredTagCategories = ['Entry requirements'];
const sortTagCategories = ['Cuisine', 'Dietary'];

export type FiltersAccordionProps = {
  suburbs?: SuburbType[];
  tags: {
    category: string | null;
    title: string | null;
    id: number | null;
  }[];
  className?: string;
  renderCollapsedOptions?: boolean;
};

export default function FiltersAccordion({
  suburbs,
  tags,
  className,
  renderCollapsedOptions = true,
}: FiltersAccordionProps) {
  const hasSuburbs = (suburbs?.length ?? 0) > 0;
  const tagsByCategory = tags
    .filter((tag) => tag.category && !ignoredTagCategories.includes(tag.category))
    .reduce((acc: Record<string, typeof tags>, tag) => {
      if (tag.category) {
        acc[tag.category] = [...(acc[tag.category] || []), tag];
      }

      return acc;
    }, {});

  const allCategoryKeys = Object.keys(tagsByCategory);

  const sortedTagsByCategory = Object.entries(tagsByCategory).sort((a, b) => {
    const aIndex = sortTagCategories.indexOf(a[0]);
    const bIndex = sortTagCategories.indexOf(b[0]);

    if (aIndex === -1) return 1; // a is not in sortTagCategories, so it goes to the bottom
    if (bIndex === -1) return -1; // b is not in sortTagCategories, so it goes to the bottom

    return aIndex - bIndex;
  });

  const defaultOpenValues = [...(hasSuburbs ? ['suburb'] : []), ...allCategoryKeys];

  return (
    <AccordionRoot
      key={defaultOpenValues.join('|')}
      type="multiple"
      defaultValue={defaultOpenValues}
      className={cn('flex flex-col', className)}>
      {hasSuburbs ? <SuburbAccordionItem suburbs={suburbs ?? []} /> : null}
      {sortedTagsByCategory.map(([category, categoryTags]) => {
        if (!categoryTags?.length) {
          return null;
        }

        return (
          <TrackProvider key={category} button_name={`${category} filter`}>
            <TagsAccordionItem
              title={category}
              tags={overloadTags(category, categoryTags)}
              renderCollapsedOptions={renderCollapsedOptions}
            />
          </TrackProvider>
        );
      })}
    </AccordionRoot>
  );
}
